<script lang="ts">
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import TextImageSidesStructure, {
		ItemDescription,
		ItemNativeName,
		ItemQuickFacts,
		ItemTags,
		ItemTitle,
		ItemViewPrimary
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import { COST_LEVEL_ICONS } from '$lib/features/_other/costLevel.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CityHighlight } from '$lib/features/cityHighlight/cityHighlight.type';
	import {
		CITY_HIGHLIGHT_BEST_TIME_METAS,
		CityHighlightBestTime,
		type CityHighlightBestTimeMeta
	} from '$lib/features/cityHighlight/cityHighlightBestTime.type';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import {
		buildExampleItemsPhrase,
		closestByLength,
		findFirstAvailableImage,
		sortShortestToLongest
	} from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconClockTimeFourOutline from '~icons/mdi/clock-time-four-outline';
	import IconTag from '~icons/mdi/tag';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.CITY_HIGHLIGHT;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<CityHighlight[]>(output.items);
	const groupedHighlights = $derived.by<Map<CityHighlightBestTime, CityHighlight[]>>(() => {
		const groups = new Map<CityHighlightBestTime, CityHighlight[]>();
		Object.values(CityHighlightBestTime).forEach((time) => groups.set(time, []));
		items.forEach((item) => groups.get(item.bestTime)?.push(item));
		return groups;
	});
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(items.map((item) => item.feature.englishName));
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: ImageDetails | undefined = findFirstAvailableImage(items);

		interface Limits {
			exampleItems: number;
		}
		function examplesLimits(limits: Limits): { exampleItems: string } {
			return {
				exampleItems: buildExampleItemsPhrase({
					names: orderedNames,
					totalItems: orderedNames.length,
					limit: limits.exampleItems
				})
			};
		}
		const examples = {
			large: examplesLimits({ exampleItems: 4 }),
			mid: examplesLimits({ exampleItems: 3 }),
			small: examplesLimits({ exampleItems: 2 })
		};
		const description: string = closestByLength([
			`Explore the top highlights in ${location.city.label}, ${examples.large.exampleItems}, with best times to visit.`,
			`Explore the top highlights in ${location.city.label}, ${examples.mid.exampleItems}.`,
			`Explore the top highlights in ${location.city.label}, ${examples.small.exampleItems}.`
		]);

		return {
			title: `${location.city.label} Experiences & Highlights: ${shortestName} & More`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdHighlightsList = $derived.by(() => {
		const json = data.output.jsonLdHighlightsList;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([output.jsonLdBreadcrumbs, jsonLdHighlightsList]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Memorable Experiences & Highlights in ${location.city.label}`}
	description={`Enhance your trip with these standout moments. We've gathered a collection of great experiences across the city, organized perfectly by the time of day.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: items.flatMap((item) => item.primary?.place?.coordinates ?? item.feature.place?.coordinates ?? []),
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<div class="flex flex-col gap-12 lg:gap-16">
		{#each Array.from(groupedHighlights.entries()) as [timeKey, groupItems], groupIndex (timeKey)}
			{#if groupItems.length > 0}
				{const { label, Icon, textColour, bgColour } = $derived<CityHighlightBestTimeMeta>(
					CITY_HIGHLIGHT_BEST_TIME_METAS[timeKey]
				)}
				<section>
					<div class="mb-12 flex items-center gap-4 py-4 text-3xl {textColour}">
						<Icon class="text-3xl" />
						<h2>{label}</h2>
						<div class="ml-4 hidden h-px flex-1 {bgColour} md:block"></div>
					</div>

					<ul class="flex flex-col gap-12 lg:gap-16">
						{#each groupItems as item, itemIndex (item.feature.id)}
							<li>
								{@render ItemRow(item, groupIndex === 0 && itemIndex === 0)}
							</li>
						{/each}
					</ul>
				</section>
			{/if}
		{/each}
	</div>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet ItemRow(item: CityHighlight, isFirstEager: boolean)}
	{const images = $derived<ImageDetails[]>(item.feature.images.length > 0 ? item.feature.images : item.primary?.images || [])}
	{const { Icon } = $derived<CityHighlightBestTimeMeta>(CITY_HIGHLIGHT_BEST_TIME_METAS[item.bestTime])}

	<article
		id={item.feature.slug}
		class="flex scroll-mt-28 flex-col gap-8 rounded-box border border-base-content/10 bg-base-100 p-6 shadow-sm transition-all hover:border-base-content/20 md:p-8"
	>
		<TextImageSidesStructure englishName={item.feature.englishName} {images} eagerLoadImages={isFirstEager}>
			{#snippet TextContentSnippet()}
				<div class="flex flex-col gap-1">
					{@render ItemTitle({ englishName: item.feature.englishName })}
					{@render ItemNativeName({
						nativeName: item.feature.nativeName,
						language: output.countryLanguage
					})}
				</div>
				{@render ItemQuickFacts({
					facts: [
						{
							text: `${item.durationHours} ${item.durationHours === 1 ? 'Hour' : 'Hours'}`,
							title: 'Recommended visit duration',
							Icon: IconClockTimeFourOutline
						},
						{ text: item.cost, title: 'Cost level', Icon: COST_LEVEL_ICONS[item.cost] },
						{
							text: item.bestTime === CityHighlightBestTime.SEASONAL ? 'Seasonal' : `Best in the ${item.bestTime}`,
							title: 'Best time to visit',
							Icon: Icon
						}
					]
				})}
				<div class="divider my-0 opacity-50"></div>
				{@render ItemDescription({ text: item.feature.description })}
				<div class="mt-2 flex flex-col gap-6">
					{@render ItemTags({ heading: { text: 'TAGS', Icon: IconTag }, tags: item.tags })}
					{@render ItemViewPrimary({ primary: item.primary, location })}
				</div>
			{/snippet}
		</TextImageSidesStructure>

		<div class="border-t border-base-content/10 pt-6">
			<PlaceExtras
				name={item.feature.englishName}
				place={item.primary?.place ?? item.feature.place}
				videos={item.feature.videos}
				typeMapPoints={output.typeMapPoints}
			/>
		</div>
	</article>
{/snippet}
